/**
 * CollaborativeWhiteboard.jsx
 * 
 * Real-time collaborative whiteboard powered by Excalidraw + Socket.io.
 * 
 * Features:
 * - Multi-user real-time collaboration
 * - Rectangles, circles, arrows, lines, text, freehand drawing
 * - Zoom/pan, undo/redo, eraser
 * - Select/move/resize objects
 * - Multi-cursor presence indicators
 * - Session persistence and reconnect state recovery
 * - Debounced autosave
 * 
 * Integration:
 * - Uses existing Socket.io connection from parent view
 * - Persists to MySQL via backend socket handlers
 * - Room-based session isolation
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Excalidraw } from '@excalidraw/excalidraw';
import { trackEvent } from '../../utils/analytics';

// ====== STYLES ======
const styles = {
  container: {
    width: '100%',
    height: '100%',
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
    background: 'var(--bg-card, #ffffff)',
    border: '1px solid var(--border-color, #e2e8f0)',
  },
  excalidrawWrapper: {
    width: '100%',
    height: '100%',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: '16px',
    color: 'var(--text-muted, #64748b)',
    fontFamily: 'Inter, Arial, sans-serif',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid var(--border-color, #e2e8f0)',
    borderTop: '3px solid #6366f1',
    borderRadius: '50%',
    animation: 'wb-spin 0.8s linear infinite',
  },
  statusBar: {
    position: 'absolute',
    bottom: '8px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'rgba(15, 23, 42, 0.8)',
    color: '#e2e8f0',
    padding: '4px 14px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backdropFilter: 'blur(8px)',
    zIndex: 10,
    fontFamily: 'Inter, Arial, sans-serif',
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
  },
  statusDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  presenceBar: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    display: 'flex',
    gap: '6px',
    zIndex: 10,
    pointerEvents: 'none',
  },
  presenceAvatar: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: '700',
    color: '#fff',
    border: '2px solid rgba(255,255,255,0.9)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    fontFamily: 'Inter, Arial, sans-serif',
  },
  toggleButton: {
    position: 'absolute',
    top: '8px',
    left: '8px',
    zIndex: 10,
    background: 'rgba(15, 23, 42, 0.8)',
    color: '#e2e8f0',
    border: 'none',
    borderRadius: '8px',
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontFamily: 'Inter, Arial, sans-serif',
    transition: 'background 0.2s',
  },
};

// Presence avatar colors
const AVATAR_COLORS = [
  '#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6',
  '#8b5cf6', '#ef4444', '#14b8a6', '#f97316', '#06b6d4',
];

const getAvatarColor = (name) => {
  const hash = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
};

/**
 * @param {Object} props
 * @param {Object} props.socket - Active Socket.io instance
 * @param {string} props.roomId - Interview room ID
 * @param {string} props.userName - Current user's display name
 * @param {string} props.role - 'candidate' | 'interviewer' | 'recruiter'
 * @param {boolean} [props.readOnly=false] - Read-only mode for recruiters
 * @param {string} [props.theme='light'] - 'light' or 'dark'
 * @param {function} [props.onClose] - Callback when whiteboard is closed/hidden
 */
const CollaborativeWhiteboard = ({
  socket,
  roomId,
  userName,
  role,
  readOnly = false,
  theme = 'light',
  onClose,
}) => {
  const [excalidrawAPI, setExcalidrawAPI] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [collaborators, setCollaborators] = useState(new Map());
  const [isRestoring, setIsRestoring] = useState(true);
  const [initialData, setInitialData] = useState(null);
  const [hasLoggedWbUse, setHasLoggedWbUse] = useState(false);

  const apiRef = useRef(null);
  const isRemoteUpdateRef = useRef(false);
  const debounceTimerRef = useRef(null);
  const pointerTimerRef = useRef(null);
  const lastEmitTimeRef = useRef(0);
  const lastPointerEmitTimeRef = useRef(0);
  const lastSentElementsRef = useRef(null);

  // Keep apiRef in sync
  useEffect(() => {
    apiRef.current = excalidrawAPI;
  }, [excalidrawAPI]);

  // ====== SOCKET CONNECTION ======
  useEffect(() => {
    if (!socket || !roomId) return;

    // Join whiteboard room
    socket.emit('wb:join', { roomId, userName, role });
    setIsConnected(true);

    // Handle state restoration from server
    const handleStateRestore = (data) => {
      console.log(`🎨 Restoring whiteboard state (${(data.elements || []).length} elements)`);
      
      setInitialData({
        elements: data.elements || [],
        appState: data.appState || {},
        files: data.files || {},
      });
      
      setIsRestoring(false);
    };

    // Handle remote scene updates from other users
    const handleSceneUpdate = (data) => {
      if (!apiRef.current) return;
      if (data.senderId === socket.id) return; // Prevent echo

      isRemoteUpdateRef.current = true;

      try {
        const currentElements = apiRef.current.getSceneElements();
        
        // Merge strategy: replace elements by ID, keep locally-created ones
        const remoteElementMap = new Map(
          (data.elements || []).map((el) => [el.id, el])
        );
        const localOnlyElements = currentElements.filter(
          (el) => !remoteElementMap.has(el.id) && el.version > (remoteElementMap.get(el.id)?.version || 0)
        );

        // Use remote elements as base, add any local-only elements
        const mergedElements = [
          ...data.elements,
          ...localOnlyElements,
        ];

        apiRef.current.updateScene({
          elements: mergedElements,
        });
        console.log(`📡 [Whiteboard] Received & applied ${data.elements.length} elements from ${data.senderId}`);
      } catch (err) {
        console.error('❌ Failed to apply remote scene update:', err);
      }

      setTimeout(() => {
        isRemoteUpdateRef.current = false;
      }, 100);
    };

    // Handle pointer updates for presence
    const handlePointerUpdate = (data) => {
      setCollaborators((prev) => {
        const next = new Map(prev);
        next.set(data.socketId, {
          userName: data.userName,
          role: data.role,
          pointer: data.pointer,
          lastSeen: Date.now(),
        });
        return next;
      });
    };

    // Handle user joined
    const handleUserJoined = (data) => {
      console.log(`🎨 ${data.userName} joined the whiteboard`);
      setCollaborators((prev) => {
        const next = new Map(prev);
        next.set(data.socketId, {
          userName: data.userName,
          role: data.role,
          pointer: null,
          lastSeen: Date.now(),
        });
        return next;
      });
    };

    // Handle user left
    const handleUserLeft = (data) => {
      console.log(`🎨 ${data.userName} left the whiteboard`);
      setCollaborators((prev) => {
        const next = new Map(prev);
        next.delete(data.socketId);
        return next;
      });
    };

    // Handle save acknowledgement
    const handleSaveAck = (data) => {
      if (data.success) {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      } else {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    };

    // Register listeners
    socket.on('wb:state-restore', handleStateRestore);
    socket.on('wb:scene-update', handleSceneUpdate);
    socket.on('wb:pointer-update', handlePointerUpdate);
    socket.on('wb:user-joined', handleUserJoined);
    socket.on('wb:user-left', handleUserLeft);
    socket.on('wb:save-ack', handleSaveAck);

    // Set a timeout for restoring state (if no snapshot exists, stop loading)
    const restoreTimeout = setTimeout(() => {
      setIsRestoring(false);
    }, 3000);

    return () => {
      socket.off('wb:state-restore', handleStateRestore);
      socket.off('wb:scene-update', handleSceneUpdate);
      socket.off('wb:pointer-update', handlePointerUpdate);
      socket.off('wb:user-joined', handleUserJoined);
      socket.off('wb:user-left', handleUserLeft);
      socket.off('wb:save-ack', handleSaveAck);
      clearTimeout(restoreTimeout);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [socket, roomId, userName, role]);

  // ====== PRESENCE CLEANUP ======
  // Remove stale collaborators (no pointer update in 30s)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setCollaborators((prev) => {
        const next = new Map(prev);
        for (const [id, data] of next) {
          if (now - data.lastSeen > 30000) {
            next.delete(id);
          }
        }
        return next;
      });
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  // ====== SCENE CHANGE HANDLER ======
  const handleChange = useCallback(
    (elements, appState, files) => {
      // Skip if this change was triggered by a remote update
      if (isRemoteUpdateRef.current) return;
      if (!socket || !roomId) return;
      if (readOnly) return;

      const elementsJson = JSON.stringify(elements.map(e => ({ id: e.id, v: e.version })));
      if (elementsJson === lastSentElementsRef.current) return;

      const emitChange = () => {
        lastSentElementsRef.current = elementsJson;
        lastEmitTimeRef.current = Date.now();
        console.log(`📤 [Whiteboard] Sending ${elements.length} elements to server`);
        if (!hasLoggedWbUse) {
          trackEvent('whiteboard_used', { role, room_id: roomId });
          setHasLoggedWbUse(true);
        }
        socket.emit('wb:scene-update', {
          roomId,
          elements,
          files: files || {},
        });
        setSaveStatus('saving');
      };

      const now = Date.now();
      if (now - lastEmitTimeRef.current > 100) {
        // Send immediately if enough time has passed
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        emitChange();
      } else {
        // Ensure the final state is sent after they stop drawing
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(emitChange, 100);
      }
    },
    [socket, roomId, readOnly]
  );

  // ====== POINTER MOVE HANDLER ======
  const handlePointerUpdate = useCallback(
    (payload) => {
      if (!socket || !roomId) return;

      const emitPointer = () => {
        lastPointerEmitTimeRef.current = Date.now();
        socket.emit('wb:pointer-update', {
          roomId,
          pointer: payload.pointer,
        });
      };

      const now = Date.now();
      if (now - lastPointerEmitTimeRef.current > 50) {
        if (pointerTimerRef.current) clearTimeout(pointerTimerRef.current);
        emitPointer();
      } else {
        if (pointerTimerRef.current) clearTimeout(pointerTimerRef.current);
        pointerTimerRef.current = setTimeout(emitPointer, 50);
      }
    }, [socket, roomId]);

  // ====== RENDER ======
  const saveStatusText = {
    idle: '',
    saving: '⏳ Saving...',
    saved: '✅ Saved',
    error: '❌ Save failed',
  };

  const collaboratorsArray = Array.from(collaborators.values());

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes wb-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .excalidraw .App-menu_top .buttonList {
          display: flex;
        }
        .excalidraw-container, .excalidraw {
          height: 100% !important;
          width: 100% !important;
        }
      `}</style>
      
      {/* Fallback CSS injection to fix unstyled Excalidraw rendering in CRA/Webpack 5 */}
      <link 
        rel="stylesheet" 
        href="https://unpkg.com/@excalidraw/excalidraw@0.18.1/dist/prod/index.css" 
      />

      {/* Presence avatars */}
      {collaboratorsArray.length > 0 && (
        <div style={styles.presenceBar}>
          {collaboratorsArray.map((collab, idx) => (
            <div
              key={idx}
              style={{
                ...styles.presenceAvatar,
                background: getAvatarColor(collab.userName),
              }}
              title={`${collab.userName} (${collab.role})`}
            >
              {getInitials(collab.userName)}
            </div>
          ))}
        </div>
      )}

      {/* Close button */}
      {onClose && (
        <button
          style={styles.toggleButton}
          onClick={onClose}
          title="Close Whiteboard"
        >
          ✕ Close
        </button>
      )}

      {/* Status bar */}
      <div style={styles.statusBar}>
        <div
          style={{
            ...styles.statusDot,
            background: isConnected ? '#22c55e' : '#ef4444',
          }}
        />
        <span>{isConnected ? 'Connected' : 'Disconnected'}</span>
        {collaboratorsArray.length > 0 && (
          <span>• {collaboratorsArray.length} collaborator{collaboratorsArray.length !== 1 ? 's' : ''}</span>
        )}
        {saveStatusText[saveStatus] && <span>• {saveStatusText[saveStatus]}</span>}
        {readOnly && <span style={{ color: '#f59e0b' }}>• Read-only</span>}
      </div>

      {/* Excalidraw canvas */}
      <div style={styles.excalidrawWrapper}>
        {isRestoring ? (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner} />
            <span>Restoring previous session...</span>
          </div>
        ) : (
            <Excalidraw
              initialData={initialData}
              excalidrawAPI={(api) => {
                if (api && !excalidrawAPI) {
                  setExcalidrawAPI(api);
                }
              }}
              onChange={handleChange}
              onPointerUpdate={handlePointerUpdate}
              theme={theme}
              viewModeEnabled={readOnly}
              zenModeEnabled={false}
              gridModeEnabled={false}
              UIOptions={{
                canvasActions: {
                  changeViewBackgroundColor: true,
                  clearCanvas: !readOnly,
                  export: { saveFileToDisk: true },
                  loadScene: false,
                  saveToActiveFile: false,
                  toggleTheme: true,
                },
              }}
              langCode="en"
            />
          )}
      </div>
    </div>
  );
};

export default CollaborativeWhiteboard;
